import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import {
  TextField,
  Button,
  Chip,
  Box,
  Typography,
  CircularProgress,
} from "@mui/material";
import { makeRequest } from "../../makeRequest";
import TextEditor from "./TextEditor";
import Title from "../../components/Title";

const TrainerUpdate = () => {
  const { id } = useParams();
  const [formData, setFormData] = useState({
    firstname: "",
    lastname: "",
    message: null,
    occupations: [],
    instagram: "",
    facebook: "",
    image1: "",
    image2: "",
  });
  const [preview, setPreview] = useState(null);
  const [preview2, setPreview2] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [image1, setImage] = useState(null);
  const [image2, setImage2] = useState(null);
  const [occupation, setOccupation] = useState("");

  useEffect(() => {
    // Fetch trainer data by ID
    const fetchTrainer = async () => {
      try {
        const res = await makeRequest.get(`/trainers/${id}`);
        setFormData(res.data);
        setPreview(res.data.image1Url || null);
        setPreview2(res.data.image2Url || null);

        if (res.data.image) {
          try {
            // Fetch signed URL for the image
            const imageResponse = await makeRequest.get(
              `/images/${res.data.image}`
            );
            setPreview(imageResponse.data.url); // Update trainer.image with the URL
          } catch (err) {
            console.error(
              `Error fetching image for trainer ${res.data.id}:`,
              err
            );
          }
        }

        if (res.data.image2) {
          try {
            // Fetch signed URL for the image2
            const image2Response = await makeRequest.get(
              `/images/${res.data.image2}`
            );
            setPreview2(image2Response.data.url); // Update trainer.image2 with the URL
          } catch (err) {
            console.error(
              `Error fetching image2 for trainer ${res.data.id}:`,
              err
            );
          }
        }
      } catch (err) {
        console.error("Error fetching trainer:", err);
      }
    };
    fetchTrainer();
  }, [id]);

  const handleValueChange = (newValue) => {
    setFormData({ ...formData, message: newValue });
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleAddOccupation = (occupation) => {
    setFormData({
      ...formData,
      occupations: [...formData.occupations, occupation],
    });
  };

  const handleRemoveOccupation = (index) => {
    setFormData({
      ...formData,
      occupations: formData.occupations.filter((_, i) => i !== index),
    });
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onloadend = () => setPreview(reader.result);
    reader.readAsDataURL(file);
    const data = new FormData();
    data.append("image", file);
    setImage(data);
  };

  const handleFile2Change = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onloadend = () => setPreview2(reader.result);
    reader.readAsDataURL(file);
    const data = new FormData();
    data.append("image", file);
    setImage2(data);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsUploading(true);
    try {
      let key = formData.image1;
      let key2 = formData.image2;
      if (image1) {
        const response = await makeRequest.post("/images/trainer", image1, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        key = response.data.name;
      }
      if (image2) {
        const response2 = await makeRequest.post("/images/trainer", image2, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        key2 = response2.data.name;
      }
      await makeRequest.put(`/trainers/${id}`, {
        ...formData,
        image1: key,
        image2: key2,
      });
      alert("Trainer updated!");
    } catch (err) {
      console.error("Error updating trainer:", err);
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="min-h-screen sm:mt-8 sm:mb-36 md:my-36 fade-up flex flex-col items-center">
      <div className="my-16">
        <Title word="Update Trainer" />

        <form onSubmit={handleSubmit}>
          <TextField
            fullWidth
            margin="normal"
            label="First Name"
            name="firstname"
            value={formData.firstname}
            onChange={handleChange}
            required
          />
          <TextField
            fullWidth
            margin="normal"
            label="Last Name"
            name="lastname"
            value={formData.lastname}
            onChange={handleChange}
            required
          />
          <p>Message</p>
          <div className="bg-white">
            {formData.message && (
              <TextEditor
                edit={formData.message}
                onValueChange={handleValueChange}
              />
            )}
          </div>
          <div className="flex justify-between gap-2 h-fit">
            <TextField
              fullWidth
              margin="normal"
              value={occupation}
              onChange={(e) => setOccupation(e.target.value)}
              label="Add Occupation"
            />
            <Button
              onClick={() => {
                handleAddOccupation(occupation);
                setOccupation("");
              }}
              variant="contained"
            >
              Ajouter
            </Button>
          </div>
          {formData.occupations.map((occ, index) => (
            <Chip
              key={index}
              label={occ}
              onDelete={() => handleRemoveOccupation(index)}
            />
          ))}
          <TextField
            fullWidth
            margin="normal"
            label="Instagram"
            name="instagram"
            value={formData.instagram}
            onChange={handleChange}
          />
          <TextField
            fullWidth
            margin="normal"
            label="Facebook"
            name="facebook"
            value={formData.facebook}
            onChange={handleChange}
          />
          <Button variant="contained" component="label" sx={{ mt: 2, mb: 2 }}>
            Upload Image
            <input
              type="file"
              accept="image/*"
              hidden
              onChange={handleFileChange}
            />
          </Button>
          {isUploading && <CircularProgress size={24} />}
          {preview && (
            <Box
              component="img"
              src={preview}
              alt="Preview"
              sx={{ width: "100%", height: "auto", mb: 2 }}
            />
          )}
          <Button variant="contained" component="label" sx={{ mt: 2, mb: 2 }}>
            Upload Image 2
            <input
              type="file"
              accept="image/*"
              hidden
              onChange={handleFile2Change}
            />
          </Button>
          {isUploading && <CircularProgress size={24} />}
          {preview2 && (
            <Box
              component="img"
              src={preview2}
              alt="Preview"
              sx={{ width: "100%", height: "auto", mb: 2 }}
            />
          )}
          <Button type="submit" variant="contained" fullWidth>
            Update
          </Button>
        </form>
      </div>
    </div>
  );
};

export default TrainerUpdate;
